/**
 * Helper function to get query parameters for the current page. Useful to get the current subscription id
 * @returns {Record<string,string>}
 */
const searchParams = () => {
  return new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
};

/**
 * Helper function similar to _.get but vanilla
 * @param {Record<any,any>|any[]} obj - the object to traverse
 * @param {string} path - the path to get the value from
 * @param {any} defaultValue - the default value to return if the path do not match anything
 * @returns {any}
 */
const get = (obj, path, defaultValue = "") => {
  const travel = (regexp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce(
        (res, key) => (res !== null && res !== undefined ? res[key] : res),
        obj
      );
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
  return result === undefined || result === obj ? defaultValue : result;
};

/**
 * Helper function to toggle a skeleton loading indicator for a list.
 * @param {HTMLElement} container - the container of the list
 * @param {boolean} loading - the loading state
 * @returns {HTMLElement| null} - the template hidden by the loading indicator if loading === true
 */
const toggleListLoading = (container, loading) => {
  if (loading) {
    // Get and clone the template to duplicate
    const shadowTemplate = container.querySelector("[data-lp-template]");
    const template = shadowTemplate.cloneNode(true);
    // Remove the reference from the DOM
    shadowTemplate.parentElement.removeChild(shadowTemplate);
    // Get the skeleton template and  build some skeletons
    const skeleton = container.querySelector(`[data-lp-skeleton="list"]`);
    for (let i = 0; i < 5; i++) {
      skeleton.parentElement.appendChild(skeleton.cloneNode());
    }
    return template;
  } else {
    // Once loaded, get all the skeletons and remove them
    const skeletons = container.querySelectorAll(`[data-lp-skeleton="list"]`);
    skeletons.forEach((skeleton) => {
      skeleton.parentElement.removeChild(skeleton);
    });
    return null;
  }
};

/**
 * Helper function to remove all loading indicators from a **non-list page**
 * @param {HTMLElement} container
 * @param {boolean} loading
 */
const toggleLoading = (container, loading) => {
  if (!loading) {
    container
      .querySelectorAll("[data-lp-skeleton]")
      .forEach((skeleton) => skeleton.parentElement.removeChild(skeleton));
  }
};

/**
 * Helper function responsible of the asynchronous data fetching, loading indicators and list building
 * @param {HTMLElement} container  - the parent of the list
 * @param {Promise<Record<any,any>[]>} fetcher - async function returning the data to build the list from
 */
const fetchAndBuildList = async (container, fetcher) => {
  const template = toggleListLoading(container, true);
  const responseData = await fetcher();
  if (!responseData) {
    const emptyText = container.querySelector("[data-lp-emptytext]");
    emptyText.removeAttribute("hidden");
  }
  toggleListLoading(container, false);
  buildList(
    container.querySelector("[data-lp-container]") || container,
    template,
    responseData || []
  );
};

/**
 * Helper function responsible of appending a list to the DOM.
 * @param {HTMLElement} container - the container of the list
 * @param {HTMLElement} template - the template to build the list from
 * @param {Record<any,any>[]} listData - the array of data to build the list from
 * @param {any|undefined} responseData - the whole response data that may contain additional information
 */
const buildList = (container, template, listData, responseData) => {
  listData.forEach((childData) => {
    const newChild = template.cloneNode(true);
    searchAndReplace(newChild, childData, responseData);
    container.appendChild(newChild);
  });
};

/**
 * Helper function responsible of the asynchronous data fetching, loading indicators and page / list building
 * @param {HTMLElement} container - container to fullfil
 * @param {Promise<any>} fetcher - async function returning the data to build the page from
 * @param {string|undefined} listAccessor - optional list accessor if the page to build contain a list.
 */
const buildPage = async (container, fetcher, listAccessor) => {
  toggleLoading(container, true);
  let listTemplate;
  if (listAccessor) {
    listTemplate = toggleListLoading(container, true);
  }

  const responseData = await fetcher();

  toggleLoading(container, false);
  listAccessor && toggleListLoading(container, false);
  listAccessor &&
    buildList(
      container.querySelector("[data-lp-container]"),
      listTemplate,
      get(responseData, listAccessor),
      responseData
    );
  searchAndReplace(container, responseData);
};

/**
 * Helper function to avoid replacing a nested template
 * @param {HTMLElement} element
 * @param {HTMLElement} container
 * @returns {boolean}
 */
const shouldReplace = (element, container) => {
  let containerReached = false;
  let templateLevel = 0;
  let node = element;
  while (node && !containerReached) {
    if (node.hasAttribute("data-lp-template")) {
      templateLevel++;
    }
    if (node === container) {
      containerReached = true;
    }
    node = node.parentElement;
  }

  return templateLevel <= 1;
};

/**
 * Helper function that look to data-lp-[accessor, attribute, type] and replace the content or attribute accordingly
 * @param {HTMLElement} child - the element we want to fullfil
 * @param {Record<any,any>} childData - the data we want to fullfiull the element
 * @param {Record<any, any>} responseData - the whole response data that may contain additional information
 */
const searchAndReplace = (child, childData, responseData) => {
  const elementsToReplace = child.querySelectorAll("[data-lp-accessor]");

  elementsToReplace.forEach((el) => {
    if (!shouldReplace(el, child)) {
      return;
    }
    const accessor = el.getAttribute("data-lp-accessor");
    const type = el.getAttribute("data-lp-type");
    const attribute = el.getAttribute("data-lp-attribute");

    /**
     * Helper function to set the accessed value either as an attribute or as textContent base on the data-lp-attribute value
     * @param {string} value - the accessed value to set
     */
    const set = (value) => {
      if (attribute === null) {
        el.textContent = value;
      } else {
        el.setAttribute(attribute, value);
      }
    };
    switch (type) {
      default: {
        set(get(childData, accessor));
        break;
      }
      case "fulldate":
      case "date": {
        set(
          new Intl.DateTimeFormat("fr-FR", {
            month: type === "fulldate" ? "long" : "short",
            day: "2-digit",
            year: "numeric",
          }).format(new Date(childData[accessor]))
        );
        break;
      }

      case "computed": {
        set(eval(accessor)(responseData));
        break;
      }
    }
    // cleaning the DOM
    el.removeAttribute("data-lp-accessor");
    el.removeAttribute("data-lp-type");
    el.removeAttribute("data-lp-attribute");
  });
};

/**
 * Helper function to get all checked subscriptions on screen
 * @returns {string[]} - return an array of subscription ids
 */
const getCheckedSubscriptions = () => {
  return Array.from(document.querySelectorAll("#subscriptionInput"))
    .filter((element) => element.checked)
    .map((element) => element.getAttribute("subscription"));
};

function toggleOptions() {
  var element = document.getElementById("subcriptionOptions");
  element.classList.toggle("subcription-options-active");
}
